import { type ReactNode } from 'react'

import { Image } from '@/shared/components/ui/image'

export interface AuthenticatedImageProps {
  /**
   * The file's stable, never-expiring proxy URL as served by the API
   * (``file_url`` / ``thumbnail_url`` on the attachment payload) - e.g.
   * ``/api/files/<uuid>/view/?t=<token>``. The capability token is carried in
   * the URL, so a bare ``<img src>``, right-click "open in new tab", and
   * "download" all work on the same link and it never expires (unlike a signed
   * GCS URL embedded in a cached payload). This is the fix for TA-23.
   */
  src: string | null | undefined
  alt: string
  className?: string
  fit?: 'cover' | 'contain'
  fallback?: ReactNode
  testId?: string
}

/**
 * Drop-in replacement for a raw ``<img>`` that displays a protected uploaded
 * file (claim evidence, proof of purchase, etc.).
 *
 * It composes the shared ``Image`` primitive (fallback handling) on top of the
 * never-expiring, token-carrying proxy URL from the API. Callers never handle
 * expiring signed URLs - they pass the payload's ``file_url`` / ``thumbnail_url``
 * and get a stable, shareable source.
 */
export function AuthenticatedImage({
  src,
  alt,
  className,
  fit = 'cover',
  fallback,
  testId,
}: AuthenticatedImageProps) {
  return (
    <Image
      src={src ?? undefined}
      alt={alt}
      className={className}
      fit={fit}
      fallback={fallback}
      testId={testId}
    />
  )
}
